/**
 * WorkspaceHeader - Mode toggle and workspace context display
 * Shows mode buttons, breadcrumb, and quick actions
 */

import { Button } from '@/components/ui/button'
import { useWorkspaceStore, type WorkspaceMode } from '@/stores/workspaceStore'
import {
  LayoutGrid,
  LayoutDashboard,
  Bot,
  Palette,
  FileText,
  Grid3x3,
  Compass,
  Settings,
} from 'lucide-react'
import { cn } from '@/lib/utils'

const MODES: Array<{
  id: WorkspaceMode
  label: string
  icon: typeof LayoutGrid
  description: string
}> = [
  {
    id: 'dashboard',
    label: 'Dashboard',
    icon: LayoutDashboard,
    description: 'Executive summary overview',
  },
  {
    id: 'flow',
    label: 'Flow',
    icon: Compass,
    description: 'Journey-guided entity flow',
  },
  {
    id: 'full-ai',
    label: 'Coach',
    icon: Bot,
    description: 'AI-driven product intelligence',
  },
  {
    id: 'canvas',
    label: 'Canvas',
    icon: Palette,
    description: 'Visual business model',
  },
  {
    id: 'smart-doc',
    label: 'Smart Doc',
    icon: FileText,
    description: 'AI-enhanced documents',
  },
  {
    id: 'entities',
    label: 'Entities',
    icon: Grid3x3,
    description: 'Structured entity management',
  },
]

export function WorkspaceHeader() {
  const { activeMode, setActiveMode } = useWorkspaceStore()

  return (
    <div className="flex-shrink-0 border-b border-warm-200 bg-white px-6 py-4">
      <div className="flex items-center justify-between">
        {/* Left: Title and Mode Toggle */}
        <div className="flex items-center gap-6">
          <div>
            <h1 className="text-xl font-bold flex items-center gap-2 text-warm-900">
              <LayoutGrid size={24} style={{ color: 'var(--color-warm-600)' }} />
              Workspace
            </h1>
            <p className="text-xs text-warm-600 mt-1">
              Product Manager IDE
            </p>
          </div>

          {/* Mode Toggle Buttons */}
          <div className="flex items-center gap-1 bg-warm-100 p-1 rounded-lg">
            {MODES.map((mode) => {
              const Icon = mode.icon
              const isActive = activeMode === mode.id

              return (
                <button
                  key={mode.id}
                  onClick={() => setActiveMode(mode.id)}
                  className={cn(
                    'flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all',
                    isActive
                      ? 'bg-white text-warm-900 shadow-sm'
                      : 'text-warm-600 hover:text-warm-900'
                  )}
                  title={mode.description}
                >
                  <Icon size={14} />
                  <span>{mode.label}</span>
                </button>
              )
            })}
          </div>
        </div>

        {/* Right: Quick Actions */}
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm">
            <Settings size={16} className="mr-2" />
            Settings
          </Button>
        </div>
      </div>
    </div>
  )
}
